import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const NotificationsModal = ({ isOpen, onClose, teamId }) => {
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && user && userProfile) {
      loadNotifications();
    }
  }, [isOpen, user, userProfile]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');

      // Get user's notification email
      const recipientEmail = userProfile?.notification_email || userProfile?.email;

      // Build query to filter by user_id or recipient_email
      let query = supabase
        ?.from('notifications')
        ?.select('*')
        ?.or(`user_id.eq.${user?.id},recipient_email.eq.${recipientEmail}`);

      // If teamId is provided, also filter by team_id
      if (teamId) {
        query = query?.eq('team_id', teamId);
      }

      // Get notifications from last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);

      const { data, error: fetchError } = await query
        ?.gte('created_at', thirtyDaysAgo?.toISOString())
        ?.order('created_at', { ascending: false });

      if (fetchError) {
        setError(fetchError?.message || 'Failed to load notifications');
        setNotifications([]);
        return;
      }

      setNotifications(data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_created': return 'Plus';
      case 'task_updated': return 'Edit';
      case 'task_deleted': return 'Trash2';
      case 'file_uploaded': return 'Upload';
      case 'meeting_scheduled': return 'Calendar';
      case 'meeting_reminder': return 'Clock';
      default: return 'Bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'task_created': return 'text-primary bg-primary/10';
      case 'task_updated': return 'text-warning bg-warning/10';
      case 'task_deleted': return 'text-error bg-error/10';
      case 'file_uploaded': return 'text-success bg-success/10';
      case 'meeting_scheduled': return 'text-blue-600 bg-blue-50';
      case 'meeting_reminder': return 'text-orange-600 bg-orange-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notificationTime) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return notificationTime?.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 sm:p-6 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Bell" size={18} className="sm:w-5 sm:h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-foreground">All Notifications</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Last 30 days
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="sm:w-5 sm:h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={28} className="sm:w-8 sm:h-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm sm:text-base text-muted-foreground">Loading notifications...</span>
            </div>
          ) : error ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
              <Icon name="AlertCircle" size={24} className="text-destructive mx-auto mb-2" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          ) : notifications?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No recent notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications?.map((notification) => (
                <div 
                  key={notification?.id} 
                  className={`p-4 border rounded-lg ${notification?.seen ? 'border-border bg-card' : 'border-primary/20 bg-primary/5'}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${getNotificationColor(notification?.type)}`}>
                      <Icon name={getNotificationIcon(notification?.type)} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {notification?.title}
                        </h4>
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification?.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification?.message && notification?.message?.trim() !== '' 
                          ? notification?.message 
                          : 'No message content available'}
                      </p>
                      {!notification?.seen && (
                        <span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                          New
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-4 sm:p-6 flex justify-end">
          <Button
            variant="default"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;